import type { ProctorAllocation } from "../native-modules/types/JSTProctorBridge";

/**
 * Parses a prforceGroups string into a Record of ProctorAllocation values.
 *
 * @param prforceGroups - A comma-separated string where each value looks like <proctor_test_name><bucket_number>
 *                       For example: "this_is_example_tst1,another_test0"
 * @returns A Record mapping test names to ProctorAllocation objects. Invalid values are skipped.
 *
 * @example
 * parsePrforceGroups("test_a1,test_b0")
 * // Returns: { "test_a": { testName: "test_a", value: 1, payload: null }, "test_b": { testName: "test_b", value: 0, payload: null } }
 */
export function parsePrforceGroups(prforceGroups: string): Record<string, ProctorAllocation> {
  // Return empty Record for empty string
  if (!prforceGroups || prforceGroups.trim() === "") {
    return {};
  }

  const result: Record<string, ProctorAllocation> = {};

  try {
    // Split by comma and process each value
    const values = prforceGroups.split(",").map((v) => v.trim());

    for (const value of values) {
      // Skip empty values
      if (value.length === 0) {
        continue;
      }

      // Extract the bucket number from the end of the string
      // The bucket number should be at the end and be a valid integer
      const match = value.match(/^(.+?)(-?\d+)$/);

      if (!match) {
        // Invalid format - skip this value and continue processing
        continue;
      }

      const [, testName, bucketStr] = match;

      // Validate that testName is not empty, not just numbers, and bucketValue is a valid number
      if (!testName || testName.length === 0 || /^\d+$/.test(testName)) {
        // Invalid format - skip this value and continue processing
        continue;
      }

      // Additional validation: ensure the original value doesn't contain invalid characters like decimals
      // Check if there are any non-alphanumeric characters (except underscores, hyphens, and the final number)
      if (!/^[a-zA-Z_][a-zA-Z0-9_]*-?\d+$/.test(value)) {
        // Invalid format - skip this value and continue processing
        continue;
      }

      const bucketValue = parseInt(bucketStr, 10);

      // Validate that bucketValue is a valid number
      if (isNaN(bucketValue)) {
        // Invalid format - skip this value and continue processing
        continue;
      }

      // Create ProctorAllocation object
      result[testName] = {
        testName,
        value: bucketValue,
        payload: null,
      };
    }

    return result;
  } catch (error) {
    // If any error occurs during parsing, return empty Record
    return {};
  }
}
